import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { createSearchKey, FUNCTION_RESULT_STATUS, IObjectCollection, IObjectKey } from '@nova/common-interfaces';
import { IInvoice, IInvoiceLine } from "@rfm/shared-interfaces";
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DropdownModule } from 'primeng/dropdown';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Table, TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { v4 as uuidv4 } from 'uuid';
import { CustomerService } from '../../../../../shared/services/customers/customers.service.mjs';
import { InvoiceService } from '../../../../../shared/services/financial/invoice.service.mjs';
import { SubscriptionService } from '../../../../../shared/services/products/subscription.service.mjs';

@Component({
  selector: 'app-invoice-edit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    AutoCompleteModule,
    TextareaModule,
    InputMaskModule,
    ButtonModule,
    ProgressSpinnerModule,
    InputNumberModule,
    DatePickerModule,
    DatePipe,
    DropdownModule,
    CheckboxModule,
    TableModule,
  ],
  templateUrl: './invoice-edit.component.html',
  styleUrls: ['./invoice-edit.component.scss'],
  encapsulation: ViewEncapsulation.None,

})
export class InvoiceEditComponent implements OnInit {
  invoiceForm!: FormGroup;
  isLoading = true;
  invoiceId!: string;
  invoice: IInvoice | undefined;
  addressOptions = <any>[];
  statuses!: string[];
  lineTypes!: string[];
  customers!: IObjectKey[];
  inEditMode: boolean = false;
  editingItem: IInvoiceLine | undefined;
  originalEditingItem: IInvoiceLine | undefined;
  // subscriptions!: IObjectKey[];
  clonedItems: { [s: number]: IInvoiceLine } = {};
  lines: IInvoiceLine[] = [];
  editingRowKeys: { [key: string]: boolean } = {}; // Track editing rows

  @ViewChild('invoiceLines') invoiceLinesTable: Table | undefined;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private invoiceService: InvoiceService,
    private customerService: CustomerService,
    private subscriptionService: SubscriptionService
  ) { }

  async ngOnInit(): Promise<void> {
    this.invoiceForm = this.fb.group({
      invoice_number: ["", Validators.required],
      due_date: [Date.now, Validators.required],
      excluding_tax: [0, Validators.required],
      discount_value: [0],
      tax: [0, Validators.required],
      including_tax: [0, Validators.required],
      status: ["", Validators.required],
      customer: ["", Validators.required],
      document_url: [""],
      sent: [false],
      is_deleted: [false],
      rows: this.fb.array([]),
    });

    this.statuses = ['Late', 'Unpaid', 'PAID'];
    this.lineTypes = ['Installation', 'Subscription', 'Hardware', 'Add-on', 'Late Fees'];
    const id = this.route.snapshot.params["id"];

    await this.fetchCustomers();
    console.log("Customers fetched successfully!");

    // this.subscriptionService.getKeys().then(result =>
    //   {
    //     if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS)
    //       {
    //         return Promise.reject("Unable to get subscription list");
    //       }

    //     this.subscriptions = result.data!;
    //     return Promise.resolve();
    //   })

    if (id != "0") {
      this.invoiceService.get(id).then(result => {
        if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
          return Promise.reject("Unable to find invoice");
        }

        this.invoice = result.data;
        if (this.invoice?.lines && this.invoice.lines.length && this.invoice.lines.data) {
          this.lines = this.invoice?.lines.data;
        }

        const date = new Date(this.invoice?.due_date!);
        const customer = this.customers.find(item => item.id === this.invoice?.customer?.id);
        this.invoiceForm.controls["due_date"].setValue(date);
        this.invoiceForm.controls["invoice_number"].setValue(this.invoice?.invoice_number);
        this.invoiceForm.controls["excluding_tax"].setValue(this.invoice?.excluding_tax);
        this.invoiceForm.controls["discount_value"].setValue(this.invoice?.discount_value);
        this.invoiceForm.controls["tax"].setValue(this.invoice?.tax);
        this.invoiceForm.controls["including_tax"].setValue(this.invoice?.including_tax);
        this.invoiceForm.controls["status"].setValue(this.invoice?.status);
        this.invoiceForm.controls["customer"].setValue(customer);
        this.invoiceForm.controls["document_url"].setValue(this.invoice?.document_url);
        this.invoiceForm.controls["is_deleted"].setValue(this.invoice?.is_deleted);
        this.invoiceForm.controls["sent"].setValue(this.invoice?.sent);

        this.lines.forEach((line, index) => {
          line.id = line.id !== '' ? line.id : uuidv4();
          this.setInvoiceLineFormGroup(line);
        });
        return Promise.resolve();
      })
    }
    else {
      const newInvoice: IInvoice = { ...this.invoiceForm.value };
      this.invoice = newInvoice;
      this.invoice.id = "0";
    }

    this.isLoading = false;
  }

  async fetchCustomers() {
    try {
      const result = await this.customerService.getKeys(createSearchKey("status", "active"));

      if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
        throw new Error("Unable to get customer list");
      }

      this.customers = result.data!;
    } catch (error) {
      console.error(error);
    }
  }

  setInvoiceLineFormGroup(line: IInvoiceLine): any {
    const start = new Date(line.period_start!);
    const end = new Date(line.period_end!);
    const group = this.fb.group({
      id: [line.id],
      line_type: [line.line_type, Validators.required],
      // subscription: ['', Validators.required],
      period_start: [start],
      period_end: [end],
      description: [line.description],
      taxable: [line.taxable],
      unit_price: [line.unit_price, Validators.required],
      quantity: [line.quantity, Validators.required],
      discount_type: [line.discount_type],
      discount_type_value: [line.discount_type_value],
      discount_total: [line.discount_total],
      line_total: [line.line_total],
    });
    (this.invoiceForm.get('rows') as FormArray).push(group);
    return group;
  }

  get url() {
    return this.invoiceForm.get('document_url');
  }

  onRowEditInit(line: IInvoiceLine, index: number) {
    // Reset all rows to non-editable
    this.editingRowKeys = {};
    // Enable edit mode for the new row
    this.editingRowKeys[line.id] = true;
    this.inEditMode = true;
    this.originalEditingItem = line;
    this.editingItem = { ...line };
  }

  onRowEditSave(line: IInvoiceLine, index: number) {
    if (line.line_total > 0) {
      this.inEditMode = false;
      this.editingItem = undefined;
      this.originalEditingItem = undefined;

      const editedRow = this.invoiceForm.value.rows[index];
      this.lines[index] = editedRow;
      this.lines[index].period_start = editedRow.period_start.getTime();
      this.lines[index].period_end = editedRow.period_end.getTime();
      this.calculateTotal();
    }
    delete this.editingRowKeys[line.id]; // Cancel editing for the row
  }

  onRowEditCancel(line: IInvoiceLine, index: number) {
    this.lines[index] = this.editingItem!;
    this.inEditMode = false;
    this.editingItem = undefined;
    this.originalEditingItem = undefined;
    this.calculateTotal();
    delete this.editingRowKeys[line.id]; // Cancel editing for the row
  }

  calculateTotal() {
    const sum = this.lines.reduce((sum, current) => sum + current.line_total, 0);
    if (this.invoice) {
      this.invoice.including_tax = sum!;
      this.invoice.excluding_tax = sum!;
      this.invoiceForm.controls["excluding_tax"].setValue(this.invoice?.excluding_tax);
      this.invoiceForm.controls["including_tax"].setValue(this.invoice?.including_tax);
    }
  }

  isEditing(line: IInvoiceLine, index: number): boolean {
    return (this.inEditMode && this.editingItem != undefined && this.lines[index] === this.originalEditingItem);
  }

  deleteRow(index: number) {
    (this.invoiceForm.get('rows') as FormArray).removeAt(index);
    this.lines.splice(index, 1); // Remove the row from the table data
    this.calculateTotal();
  }

  addNewLine() {
    const newObject = {};
    const newLine = newObject as IInvoiceLine;
    newLine.id = uuidv4();
    newLine.description = "Test1 Item";
    newLine.line_type = "Subscription";
    newLine.period_start = Date.now();
    newLine.period_end = Date.now();
    newLine.description = '';
    newLine.taxable = false;
    newLine.unit_price = 1;
    newLine.quantity = 1;
    newLine.discount_type = '';
    newLine.discount_type_value = 0;
    newLine.discount_total = 0;
    newLine.line_total = 1;
    this.lines.push(newLine);
    this.setInvoiceLineFormGroup(newLine);
    this.inEditMode = true;
    this.editingItem = newLine;
    this.originalEditingItem = newLine;
    this.onRowEditInit(newLine, this.lines.length - 1);
  }

  onSubmit(): void {
    if (this.invoiceForm.valid) {
      this.inEditMode = false;
      this.isLoading = true;

      const updatedInvoice: IInvoice = { ...this.invoiceForm.value };

      updatedInvoice.due_date = this.invoiceForm.get("due_date")?.value.getTime();
      updatedInvoice.customer = this.invoiceForm.get("customer")?.value;
      updatedInvoice.invoice_number = this.invoiceForm.get("invoice_number")?.value;
      updatedInvoice.excluding_tax = this.invoiceForm.get("excluding_tax")?.value;
      updatedInvoice.discount_value = this.invoiceForm.get("discount_value")?.value;
      updatedInvoice.tax = this.invoiceForm.get("tax")?.value;
      updatedInvoice.including_tax = this.invoiceForm.get("including_tax")?.value;
      updatedInvoice.status = this.invoiceForm.get("status")?.value;
      updatedInvoice.is_deleted = this.invoiceForm.get("is_deleted")?.value;
      updatedInvoice.sent = this.invoiceForm.get("sent")?.value;

      const linesCollection = {
        length: 0,
        data: this.lines,
      } as IObjectCollection<IInvoiceLine>;

      updatedInvoice.lines = linesCollection;
      updatedInvoice.payment_attempts = this.invoice?.payment_attempts!;
      updatedInvoice.document = this.invoice?.document;
      updatedInvoice.document_url = this.invoice?.document_url;
      updatedInvoice.comments = this.invoice?.comments;
      updatedInvoice.reference_keys = this.invoice?.reference_keys;

      if (this.invoice?.id === "0") {
        this.insertInvoice(updatedInvoice);
      }
      else {
        updatedInvoice.id = this.invoice?.id!;
        this.updateInvoice(updatedInvoice)
      }
    }
  }

  insertInvoice(invoice: IInvoice) {
    this.isLoading = true;
    this.invoiceService.insert(invoice)
      .then(() => {
        console.log('Invoice created successfully');
        this.router.navigate(['/admin/customers/view/', invoice.customer?.id]);
      })
      .catch((error) => {
        console.error('Error creating the invoice:', error);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  updateInvoice(invoice: IInvoice) {
    this.isLoading = true;
    this.invoiceService.update(invoice)
      .then(() => {
        console.log('Invoice updated successfully');
        this.router.navigate(['/admin/customers/view/', invoice.customer?.id]);
      })
      .catch((error) => {
        console.error('Error updating the invoice:', error);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
}
